import { GetProgramOnboardingTemplateById_template, GetProgramOnboardingTemplateById_template_config_brief, GetProgramOnboardingTemplateById_template_config_confirmation, GetProgramOnboardingTemplateById_template_config_hero } from '@frontend/app/queries/types/GetProgramOnboardingTemplateById';
import { DbColumnInput, ProgramOnboardingMemberFieldSchemaInput, ProgramOnboardingTemplateInput, ProgramOnboardingTemplateType } from '@frontend/app/types/globalTypes';
import { cloneDeep, omit } from 'lodash';
import React, { createContext, useContext, useMemo, useReducer } from 'react';
import { useAuth } from '@frontend/context/authContext';
import { useGetCurrentClient } from '@frontend/app/hooks';
import { IField } from '../../LandingPages/types';

export const HomeImage = 'https://storage.googleapis.com/aspirex-static-files/onboarding-template/Home.png';
export const ConfirmationImage = 'https://storage.googleapis.com/aspirex-static-files/onboarding-template/Confirmation.png';
export const BriefPageImage = 'https://storage.googleapis.com/aspirex-static-files/onboarding-template/BriefPage.png';
export const ProductsPageImage = 'https://storage.googleapis.com/aspirex-static-files/onboarding-template/ProductsPage.png';
export const FormsPageImage = 'https://storage.googleapis.com/aspirex-static-files/onboarding-template/FormsPage.png';

export interface FormValues {
  [fieldId: string]: string | Date | boolean | number | string[];
}

export interface ListItem {
  text: string;
  icon?: string;
}

export interface Confirmation {
  primaryMessage: string;
  description: string;
}

export interface PulseTemplateHero {
  carouselImages: string[];
  title: string;
  description: string;
  hasDescription: boolean;
  listStyleType: 'number' | 'bullet' | 'icon' | 'none';
  listItems: ListItem[];
  ctaButtonText: string;
}

export interface PulseTemplatePage {
  hero?: PulseTemplateHero;
  confirmation?: Confirmation;
  brief?: BriefPage;
}

export interface PulseTemplate {
  pages: PulseTemplatePage[];
}

export interface Settings {
  applicationTitle: string;
  logo: string;
  brandName: string;
  campaignDates: {
    startDate?: Date;
    endDate?: Date;
  };
  socialAccounts: {
    instagram: string;
    tiktok: string;
    youtube: string;
    pinterest: string;
    website: string;
  };
  pageUrl: string;
  bgPrimary: string;
  bgSecondary: string;
  titlePrimary: string;
  titleSecondary: string;
  bodyPrimary: string;
  bodySecondary: string;
  buttonBg: string;
  buttonTextColor: string;
  titleFont: {
    fontFamily: string;
    fontSize: number;
  };
  bodyFont: {
    fontFamily: string;
    fontSize: number;
  };
  buttonFont: {
    fontFamily: string;
    fontSize: number;
  };
}

export interface AmplifyTemplate {
  pages: AmplifyTemplatePage[];
}

export interface BriefPage {
  title: string;
  description: string;
  hasDescription: boolean;
  primaryButtonLabel: string;
}

export interface AmplifyTemplatePage {
  home?: AmplifyTemplateHome;
  confirmation?: Confirmation;
  brief?: BriefPage;
}

export interface AmplifyTemplateHome {
  hero?: AmplifyTemplateHero;
  about?: AmplifyTemplateAbout;
  perks?: AmplifyTemplatePerks;
  persona?: AmplifyTemplatePersona;
  form?: AmplifyTemplateForm;
}

export interface AmplifyTemplateHero {
  carouselImages: string[];
  title: string;
  description: string;
  hasDescription: boolean;
  primaryButtonLabel: string;
  showSection: boolean;
}

export interface AmplifyTemplateAbout {
  title: string;
  description: string;
  hasDescription: boolean;
  carouselImages: string[];
  showSection: boolean;
}

export interface AmplifyTemplatePerks {
  title: string;
  description: string;
  hasDescription: boolean;
  listStyleType: 'number' | 'bullet' | 'icon' | 'none';
  listItems: ListItem[];
  showSection: boolean;
}

export interface AmplifyTemplatePersona {
  title: string;
  description: string;
  hasDescription: boolean;
  listStyleType: 'number' | 'bullet' | 'icon' | 'none';
  listItems: ListItem[];
  personaImage: string;
  showSection: boolean;
}

export interface AmplifyTemplateForm {
  title: string;
  description: string;
  hasDescription: boolean
  showSection: boolean;
  fields: IField[];
}

export interface RecruitmentState {
  pulseTemplate: PulseTemplate | null;
  amplifyTemplate: AmplifyTemplate | null;
  selectedPage: number;
  settings: Settings;
  showNewFormFieldModal: boolean;
}

type ActionType = {
  type: 'ADD_PULSE_HERO_CONTENT_CAROUSEL';
  payload: string;
} | {
  type: 'REMOVE_PULSE_HERO_CONTENT_CAROUSEL';
  payload: number;
} | {
  type: 'SET_PULSE_HERO_CTA_BUTTON_TEXT';
  payload: string;
} | {
  type: 'INIT_PULSE_TEMPLATE';
  payload?: GetProgramOnboardingTemplateById_template;
} | {
  type: 'INIT_AMPLIFY_TEMPLATE';
  payload?: {
    template: GetProgramOnboardingTemplateById_template;
    savedFields?: IField[];
  };
} | {
  type: 'SELECT_PAGE';
  payload: number;
} | {
  type: 'SET_PULSE_HERO_TITLE';
  payload: string;
} | {
  type: 'SET_PULSE_HERO_DESCRIPTION';
  payload: string;
} | {
  type: 'SET_PULSE_HERO_HAS_DESCRIPTION';
  payload: boolean;
} | {
  type: 'SET_PULSE_HERO_LIST_STYLE_TYPE';
  payload: 'number' | 'bullet' | 'icon' | 'none';
} | {
  type: 'ADD_PULSE_HERO_LIST_ITEM';
  payload: ListItem;
} | {
  type: 'REMOVE_PULSE_HERO_LIST_ITEM';
  payload: number;
} | {
  type: 'SET_PULSE_HERO_LIST_ITEM_TEXT';
  payload: {
    index: number;
    text: string;
    icon: string;
  };
} | {
  type: 'UPDATE_SETTINGS';
  payload: Partial<Settings>;
} | {
  type: 'SET_AMPLIFY_HERO_TITLE';
  payload: string;
} | {
  type: 'SET_AMPLIFY_HERO_DESCRIPTION';
  payload: string;
} | {
  type: 'SET_AMPLIFY_HERO_HAS_DESCRIPTION';
  payload: boolean;
} | {
  type: 'SET_AMPLIFY_HERO_PRIMARY_BUTTON_LABEL';
  payload: string;
} | {
  type: 'SET_AMPLIFY_ABOUT_TITLE';
  payload: string;
} | {
  type: 'SET_AMPLIFY_ABOUT_DESCRIPTION';
  payload: string;
} | {
  type: 'SET_AMPLIFY_ABOUT_HAS_DESCRIPTION';
  payload: boolean;
} | {
  type: 'SET_AMPLIFY_PERKS_TITLE';
  payload: string;
} | {
  type: 'SET_AMPLIFY_PERKS_DESCRIPTION';
  payload: string;
} | {
  type: 'SET_AMPLIFY_PERKS_HAS_DESCRIPTION';
  payload: boolean;
} | {
  type: 'SET_AMPLIFY_PERKS_LIST_STYLE_TYPE';
  payload: 'number' | 'bullet' | 'icon' | 'none';
} | {
  type: 'ADD_AMPLIFY_PERKS_LIST_ITEM';
  payload: ListItem;
} | {
  type: 'REMOVE_AMPLIFY_PERKS_LIST_ITEM';
  payload: number;
} | {
  type: 'SET_AMPLIFY_PERKS_LIST_ITEM_TEXT';
  payload: {
    index: number;
    text: string;
    icon: string;
  };
} | {
  type: 'SET_AMPLIFY_PERSONA_TITLE';
  payload: string;
} | {
  type: 'SET_AMPLIFY_PERSONA_DESCRIPTION';
  payload: string;
} | {
  type: 'SET_AMPLIFY_PERSONA_HAS_DESCRIPTION';
  payload: boolean;
} | {
  type: 'SET_AMPLIFY_PERSONA_LIST_STYLE_TYPE';
  payload: 'number' | 'bullet' | 'icon' | 'none';
} | {
  type: 'SET_AMPLIFY_PERSONA_IMAGE';
  payload: string;
} | {
  type: 'ADD_AMPLIFY_PERSONA_LIST_ITEM';
  payload: ListItem;
} | {
  type: 'REMOVE_AMPLIFY_PERSONA_LIST_ITEM';
  payload: number;
} | {
  type: 'SET_AMPLIFY_PERSONA_LIST_ITEM_TEXT';
  payload: {
    index: number;
    text: string;
    icon: string;
  };
} | {
  type: 'SET_AMPLIFY_FORM_TITLE';
  payload: string;
} | {
  type: 'SET_AMPLIFY_FORM_DESCRIPTION';
  payload: string;
} | {
  type: 'SET_AMPLIFY_FORM_HAS_DESCRIPTION';
  payload: boolean;
} | {
  type: 'ADD_AMPLIFY_HERO_CONTENT_CAROUSEL';
  payload: string;
} | {
  type: 'REMOVE_AMPLIFY_HERO_CONTENT_CAROUSEL';
  payload: number;
} | {
  type: 'SET_AMPLIFY_HERO_SHOW_SECTION';
  payload: boolean;
} | {
  type: 'SET_AMPLIFY_ABOUT_SHOW_SECTION';
  payload: boolean;
} | {
  type: 'SET_AMPLIFY_PERKS_SHOW_SECTION';
  payload: boolean;
} | {
  type: 'SET_AMPLIFY_PERSONA_SHOW_SECTION';
  payload: boolean;
} | {
  type: 'SET_AMPLIFY_FORM_SHOW_SECTION';
  payload: boolean;
} | {
  type: 'ADD_AMPLIFY_ABOUT_CAROUSEL_IMAGES';
  payload: string;
} | {
  type: 'REMOVE_AMPLIFY_ABOUT_CAROUSEL_IMAGES';
  payload: number;
} | {
  type: 'SET_FORM_FIELDS';
  payload: IField[];
} | {
  type: 'ADD_FORM_FIELD';
  payload: IField;
} | {
  type: 'REMOVE_FORM_FIELD';
  payload: number;
} | {
  type: 'EDIT_FORM_FIELD';
  payload: {
    oldFieldId: number;
    newField: IField;
  };
} | {
  type: 'REORDER_FORM_FIELDS';
  payload: IField[];
} | {
  type: 'SET_SHOW_NEW_FORM_FIELD_MODAL';
  payload: boolean;
} | {
  type: 'SET_CONFIRMATION_PRIMARY_MESSAGE';
  payload: string;
} | {
  type: 'SET_CONFIRMATION_DESCRIPTION';
  payload: string;
} | {
  type: 'ADD_BRIEF_PAGE';
} | {
  type: 'ADD_PRODUCT_PAGE';
} | {
  type: 'ADD_FORM_PAGE';
} | {
  type: 'SET_BRIEF_TITLE';
  payload: string;
} | {
  type: 'SET_BRIEF_DESCRIPTION';
  payload: string;
} | {
  type: 'SET_BRIEF_HAS_DESCRIPTION';
  payload: boolean;
} | {
  type: 'SET_BRIEF_PRIMARY_BUTTON_LABEL';
  payload: string;
};

function recruitmentReducer(state: RecruitmentState, action: ActionType): RecruitmentState {
  switch (action.type) {
    case 'INIT_PULSE_TEMPLATE': {
      if (action.payload) {
        return transformTemplateToState(action.payload);
      }
      return {
        ...state,
        pulseTemplate: {
          pages: [
            {
              hero: {
                carouselImages: [],
                title: '',
                description: '',
                hasDescription: false,
                listStyleType: 'number',
                listItems: [],
                ctaButtonText: '',
              },
            },
            {
              confirmation: {
                primaryMessage: '',
                description: '',
              },
            }
          ] as PulseTemplatePage[],
        },
        selectedPage: 0,
      };
    }
    case 'INIT_AMPLIFY_TEMPLATE': {
      if (action.payload) {
        return transformTemplateToState(action.payload.template, action.payload.savedFields);
      }
      return {
        ...state,
        amplifyTemplate: {
          pages: [
            {
              home: {
                hero: {
                  carouselImages: [],
                  title: '',
                  description: '',
                  hasDescription: false,
                  primaryButtonLabel: '',
                  showSection: true,
                },
                about: {
                  title: '',
                  description: '',
                  hasDescription: false,
                  carouselImages: [],
                  showSection: true,
                },
                perks: {
                  title: '',
                  description: '',
                  hasDescription: false,
                  listStyleType: 'number',
                  listItems: [],
                  showSection: true,
                },
                persona: {
                  title: '',
                  description: '',
                  hasDescription: false,
                  listStyleType: 'number',
                  listItems: [],
                  personaImage: '',
                  showSection: true,
                },
                form: {
                  title: '',
                  description: '',
                  hasDescription: false,
                  showSection: true,
                  fields: [],
                },
              },
            },
            {
              confirmation: {
                primaryMessage: '',
                description: '',
              },
            }
          ] as AmplifyTemplatePage[],
        },
        selectedPage: 0,
      };
    }
    case 'ADD_PULSE_HERO_CONTENT_CAROUSEL': {
      const clonedState = cloneDeep(state);
      const heroPage = clonedState.pulseTemplate.pages.find((page) => !!page.hero);
      if (heroPage) {
        if (!heroPage.hero.carouselImages) {
          heroPage.hero.carouselImages = [];
        }
        heroPage.hero.carouselImages.push(action.payload);
      } else {
        const newHeroPage: PulseTemplatePage = {
          hero: {
            carouselImages: [action.payload],
            title: '',
            description: '',
            hasDescription: false,
            listStyleType: 'number',
            listItems: [],
          },
        } as PulseTemplatePage;
        clonedState.pulseTemplate.pages.push(newHeroPage);
      }
      return clonedState;
    }
    case 'REMOVE_PULSE_HERO_CONTENT_CAROUSEL': {
      const clonedState = cloneDeep(state);
      const heroPage = clonedState.pulseTemplate.pages.find((page) => !!page.hero);
      if (heroPage) {
        heroPage.hero.carouselImages = heroPage.hero.carouselImages.filter((_, index) => index !== action.payload);
      }
      return clonedState;
    }
    case 'SELECT_PAGE': {
      return {
        ...state,
        selectedPage: action.payload,
      };
    }
    case 'SET_PULSE_HERO_TITLE': {
      const clonedState = cloneDeep(state);
      const heroPage = clonedState.pulseTemplate.pages.find((page) => !!page.hero);
      if (heroPage) {
        heroPage.hero.title = action.payload;
      }
      return clonedState;
    }
    case 'SET_PULSE_HERO_DESCRIPTION': {
      const clonedState = cloneDeep(state);
      const heroPage = clonedState.pulseTemplate.pages.find((page) => !!page.hero);
      if (heroPage) {
        heroPage.hero.description = action.payload;
      }
      return clonedState;
    }
    case 'SET_PULSE_HERO_HAS_DESCRIPTION': {
      const clonedState = cloneDeep(state);
      const heroPage = clonedState.pulseTemplate.pages.find((page) => !!page.hero);
      if (heroPage) {
        heroPage.hero.hasDescription = action.payload;
      }
      return clonedState;
    }
    case 'SET_PULSE_HERO_LIST_STYLE_TYPE': {
      const clonedState = cloneDeep(state);
      const heroPage = clonedState.pulseTemplate.pages.find((page) => !!page.hero);
      if (heroPage) {
        heroPage.hero.listStyleType = action.payload;
      }
      return clonedState;
    }
    case 'ADD_PULSE_HERO_LIST_ITEM': {
      const clonedState = cloneDeep(state);
      const heroPage = clonedState.pulseTemplate?.pages?.find((page) => !!page.hero);
      if (heroPage) {
        if (!heroPage.hero.listItems) {
          heroPage.hero.listItems = [];
        }
        heroPage.hero.listItems.push(action.payload);
      }
      return clonedState;
    }
    case 'REMOVE_PULSE_HERO_LIST_ITEM': {
      const clonedState = cloneDeep(state);
      const heroPage = clonedState.pulseTemplate.pages.find((page) => !!page.hero);
      if (heroPage) {
        heroPage.hero.listItems = heroPage.hero.listItems.filter((_, index) => index !== action.payload);
      }
      return clonedState;
    }
    case 'SET_PULSE_HERO_LIST_ITEM_TEXT': {
      const clonedState = cloneDeep(state);
      const heroPage = clonedState.pulseTemplate.pages.find((page) => !!page.hero);
      if (heroPage) {
        heroPage.hero.listItems[action.payload.index].text = action.payload.text;
        heroPage.hero.listItems[action.payload.index].icon = action.payload.icon;
      }
      return clonedState;
    }
    case 'SET_PULSE_HERO_CTA_BUTTON_TEXT': {
      const clonedState = cloneDeep(state);
      const heroPage = clonedState.pulseTemplate.pages.find((page) => !!page.hero);
      if (heroPage) {
        heroPage.hero.ctaButtonText = action.payload;
      }
      return clonedState;
    }
    case 'UPDATE_SETTINGS': {
      return {
        ...state,
        settings: { ...state.settings, ...action.payload },
      };
    }
    case 'SET_AMPLIFY_HERO_TITLE': {
      const clonedState = cloneDeep(state);
      const heroPage = clonedState?.amplifyTemplate?.pages?.find((page) => !!page.home.hero);
      if (heroPage) {
        heroPage.home.hero.title = action.payload;
      }
      return clonedState;
    }
    case 'SET_AMPLIFY_HERO_DESCRIPTION': {
      const clonedState = cloneDeep(state);
      const heroPage = clonedState?.amplifyTemplate?.pages?.find((page) => !!page.home.hero);
      if (heroPage) {
        heroPage.home.hero.description = action.payload;
      }
      return clonedState;
    }
    case 'SET_AMPLIFY_HERO_HAS_DESCRIPTION': {
      const clonedState = cloneDeep(state);
      const heroPage = clonedState?.amplifyTemplate?.pages?.find((page) => !!page.home.hero);
      if (heroPage) {
        heroPage.home.hero.hasDescription = action.payload;
      }
      return clonedState;
    }
    case 'SET_AMPLIFY_HERO_PRIMARY_BUTTON_LABEL': {
      const clonedState = cloneDeep(state);
      const heroPage = clonedState?.amplifyTemplate?.pages?.find((page) => !!page.home.hero);
      if (heroPage) {
        heroPage.home.hero.primaryButtonLabel = action.payload;
      }
      return clonedState;
    }
    case 'SET_AMPLIFY_ABOUT_TITLE': {
      const clonedState = cloneDeep(state);
      const aboutPage = clonedState?.amplifyTemplate?.pages?.find((page) => !!page.home.about);
      if (aboutPage) {
        aboutPage.home.about.title = action.payload;
      }
      return clonedState;
    }
    case 'SET_AMPLIFY_ABOUT_DESCRIPTION': {
      const clonedState = cloneDeep(state);
      const aboutPage = clonedState?.amplifyTemplate?.pages?.find((page) => !!page.home.about);
      if (aboutPage) {
        aboutPage.home.about.description = action.payload;
      }
      return clonedState;
    }
    case 'SET_AMPLIFY_ABOUT_HAS_DESCRIPTION': {
      const clonedState = cloneDeep(state);
      const aboutPage = clonedState?.amplifyTemplate?.pages?.find((page) => !!page.home.about);
      if (aboutPage) {
        aboutPage.home.about.hasDescription = action.payload;
      }
      return clonedState;
    }
    case 'SET_AMPLIFY_PERKS_TITLE': {
      const clonedState = cloneDeep(state);
      const perksPage = clonedState?.amplifyTemplate?.pages?.find((page) => !!page.home.perks);
      if (perksPage) {
        perksPage.home.perks.title = action.payload;
      }
      return clonedState;
    }
    case 'SET_AMPLIFY_PERKS_DESCRIPTION': {
      const clonedState = cloneDeep(state);
      const perksPage = clonedState?.amplifyTemplate?.pages?.find((page) => !!page.home.perks);
      if (perksPage) {
        perksPage.home.perks.description = action.payload;
      }
      return clonedState;
    }
    case 'SET_AMPLIFY_PERKS_HAS_DESCRIPTION': {
      const clonedState = cloneDeep(state);
      const perksPage = clonedState?.amplifyTemplate?.pages?.find((page) => !!page.home.perks);
      if (perksPage) {
        perksPage.home.perks.hasDescription = action.payload;
      }
      return clonedState;
    }
    case 'SET_AMPLIFY_PERKS_LIST_STYLE_TYPE': {
      const clonedState = cloneDeep(state);
      const perksPage = clonedState?.amplifyTemplate?.pages?.find((page) => !!page.home.perks);
      if (perksPage) {
        perksPage.home.perks.listStyleType = action.payload;
      }
      return clonedState;
    }
    case 'ADD_AMPLIFY_PERKS_LIST_ITEM': {
      const clonedState = cloneDeep(state);
      const perksPage = clonedState?.amplifyTemplate?.pages?.find((page) => !!page.home.perks);
      if (perksPage) {
        perksPage.home.perks.listItems.push(action.payload);
      }
      return clonedState;
    }
    case 'REMOVE_AMPLIFY_PERKS_LIST_ITEM': {
      const clonedState = cloneDeep(state);
      const perksPage = clonedState?.amplifyTemplate?.pages?.find((page) => !!page.home.perks);
      if (perksPage) {
        perksPage.home.perks.listItems = perksPage.home.perks.listItems.filter((_, index) => index !== action.payload);
      }
      return clonedState;
    }
    case 'SET_AMPLIFY_PERKS_LIST_ITEM_TEXT': {
      const clonedState = cloneDeep(state);
      const perksPage = clonedState?.amplifyTemplate?.pages?.find((page) => !!page.home.perks);
      if (perksPage) {
        perksPage.home.perks.listItems[action.payload.index].text = action.payload.text;
        perksPage.home.perks.listItems[action.payload.index].icon = action.payload.icon;
      }
      return clonedState;
    }
    case 'SET_AMPLIFY_PERSONA_TITLE': {
      const clonedState = cloneDeep(state);
      const personaPage = clonedState?.amplifyTemplate?.pages?.find((page) => !!page.home.persona);
      if (personaPage) {
        personaPage.home.persona.title = action.payload;
      }
      return clonedState;
    }
    case 'SET_AMPLIFY_PERSONA_DESCRIPTION': {
      const clonedState = cloneDeep(state);
      const personaPage = clonedState?.amplifyTemplate?.pages?.find((page) => !!page.home.persona);
      if (personaPage) {
        personaPage.home.persona.description = action.payload;
      }
      return clonedState;
    }
    case 'SET_AMPLIFY_PERSONA_HAS_DESCRIPTION': {
      const clonedState = cloneDeep(state);
      const personaPage = clonedState?.amplifyTemplate?.pages?.find((page) => !!page.home.persona);
      if (personaPage) {
        personaPage.home.persona.hasDescription = action.payload;
      }
      return clonedState;
    }
    case 'SET_AMPLIFY_PERSONA_LIST_STYLE_TYPE': {
      const clonedState = cloneDeep(state);
      const personaPage = clonedState?.amplifyTemplate?.pages?.find((page) => !!page.home.persona);
      if (personaPage) {
        personaPage.home.persona.listStyleType = action.payload;
      }
      return clonedState;
    }
    case 'SET_AMPLIFY_PERSONA_IMAGE': {
      const clonedState = cloneDeep(state);
      const personaPage = clonedState?.amplifyTemplate?.pages?.find((page) => !!page.home.persona);
      if (personaPage) {
        personaPage.home.persona.personaImage = action.payload;
      }
      return clonedState;
    }
    case 'ADD_AMPLIFY_PERSONA_LIST_ITEM': {
      const clonedState = cloneDeep(state);
      const personaPage = clonedState?.amplifyTemplate?.pages?.find((page) => !!page.home.persona);
      if (personaPage) {
        personaPage.home.persona.listItems.push(action.payload);
      }
      return clonedState;
    }
    case 'REMOVE_AMPLIFY_PERSONA_LIST_ITEM': {
      const clonedState = cloneDeep(state);
      const personaPage = clonedState?.amplifyTemplate?.pages?.find((page) => !!page.home.persona);
      if (personaPage) {
        personaPage.home.persona.listItems = personaPage.home.persona.listItems.filter((_, index) => index !== action.payload);
      }
      return clonedState;
    }
    case 'SET_AMPLIFY_PERSONA_LIST_ITEM_TEXT': {
      const clonedState = cloneDeep(state);
      const personaPage = clonedState?.amplifyTemplate?.pages?.find((page) => !!page.home.persona);
      if (personaPage) {
        personaPage.home.persona.listItems[action.payload.index].text = action.payload.text;
        personaPage.home.persona.listItems[action.payload.index].icon = action.payload.icon;
      }
      return clonedState;
    }
    case 'SET_AMPLIFY_FORM_TITLE': {
      const clonedState = cloneDeep(state);
      const formPage = clonedState?.amplifyTemplate?.pages?.find((page) => !!page.home.form);
      if (formPage) {
        formPage.home.form.title = action.payload;
      }
      return clonedState;
    }
    case 'SET_AMPLIFY_FORM_DESCRIPTION': {
      const clonedState = cloneDeep(state);
      const formPage = clonedState?.amplifyTemplate?.pages?.find((page) => !!page.home.form);
      if (formPage) {
        formPage.home.form.description = action.payload;
      }
      return clonedState;
    }
    case 'SET_AMPLIFY_FORM_HAS_DESCRIPTION': {
      const clonedState = cloneDeep(state);
      const formPage = clonedState?.amplifyTemplate?.pages?.find((page) => !!page.home.form);
      if (formPage) {
        formPage.home.form.hasDescription = action.payload;
      }
      return clonedState;
    }
    case 'ADD_AMPLIFY_HERO_CONTENT_CAROUSEL': {
      const clonedState = cloneDeep(state);
      const heroPage = clonedState?.amplifyTemplate?.pages?.find((page) => !!page.home.hero);
      if (heroPage) {
        heroPage.home.hero.carouselImages.push(action.payload);
      }
      return clonedState;
    }
    case 'REMOVE_AMPLIFY_HERO_CONTENT_CAROUSEL': {
      const clonedState = cloneDeep(state);
      const heroPage = clonedState?.amplifyTemplate?.pages?.find((page) => !!page.home.hero);
      if (heroPage) {
        heroPage.home.hero.carouselImages = heroPage.home.hero.carouselImages.filter((_, index) => index !== action.payload);
      }
      return clonedState;
    }
    case 'SET_AMPLIFY_HERO_SHOW_SECTION': {
      const clonedState = cloneDeep(state);
      const heroPage = clonedState?.amplifyTemplate?.pages?.find((page) => !!page.home.hero);
      if (heroPage) {
        heroPage.home.hero.showSection = action.payload;
      }
      return clonedState;
    }
    case 'SET_AMPLIFY_ABOUT_SHOW_SECTION': {
      const clonedState = cloneDeep(state);
      const aboutPage = clonedState?.amplifyTemplate?.pages?.find((page) => !!page.home.about);
      if (aboutPage) {
        aboutPage.home.about.showSection = action.payload;
      }
      return clonedState;
    }
    case 'SET_AMPLIFY_PERKS_SHOW_SECTION': {
      const clonedState = cloneDeep(state);
      const perksPage = clonedState?.amplifyTemplate?.pages?.find((page) => !!page.home.perks);
      if (perksPage) {
        perksPage.home.perks.showSection = action.payload;
      }
      return clonedState;
    }
    case 'ADD_AMPLIFY_ABOUT_CAROUSEL_IMAGES': {
      const clonedState = cloneDeep(state);
      const aboutPage = clonedState?.amplifyTemplate?.pages?.find((page) => !!page.home.about);
      if (aboutPage) {
        aboutPage.home.about.carouselImages.push(action.payload);
      }
      return clonedState;
    }
    case 'REMOVE_AMPLIFY_ABOUT_CAROUSEL_IMAGES': {
      const clonedState = cloneDeep(state);
      const aboutPage = clonedState?.amplifyTemplate?.pages?.find((page) => !!page.home.about);
      if (aboutPage) {
        aboutPage.home.about.carouselImages = aboutPage.home.about.carouselImages.filter((_, index) => index !== action.payload);
      }
      return clonedState;
    }
    case 'SET_FORM_FIELDS': {
      const clonedState = cloneDeep(state);
      const formPage = clonedState?.amplifyTemplate?.pages?.find((page) => !!page.home.form);
      if (formPage) {
        formPage.home.form.fields = action.payload;
      }
      return clonedState;
    }
    case 'ADD_FORM_FIELD': {
      const clonedState = cloneDeep(state);
      const formPage = clonedState?.amplifyTemplate?.pages?.find((page) => !!page.home.form);
      if (formPage) {
        formPage.home.form.fields.push(action.payload);
      }
      return clonedState;
    }
    case 'REMOVE_FORM_FIELD': {
      const clonedState = cloneDeep(state);
      const formPage = clonedState?.amplifyTemplate?.pages?.find((page) => !!page.home.form);
      if (formPage) {
        formPage.home.form.fields = formPage.home.form.fields.filter((field) => field.id !== action.payload);
      }
      return clonedState;
    }
    case 'EDIT_FORM_FIELD': {
      const clonedState = cloneDeep(state);
      const formPage = clonedState?.amplifyTemplate?.pages?.find((page) => !!page.home.form);
      if (formPage) {
        formPage.home.form.fields = formPage.home.form.fields.map((field) => (field.id === action.payload.oldFieldId ? action.payload.newField : field));
      }
      return clonedState;
    }
    case 'REORDER_FORM_FIELDS': {
      const clonedState = cloneDeep(state);
      const formPage = clonedState?.amplifyTemplate?.pages?.find((page) => !!page.home.form);
      if (formPage) {
        formPage.home.form.fields = action.payload;
      }
      return clonedState;
    }
    case 'SET_SHOW_NEW_FORM_FIELD_MODAL': {
      const clonedState = cloneDeep(state);
      clonedState.showNewFormFieldModal = action.payload;
      return clonedState;
    }
    case 'SET_AMPLIFY_FORM_SHOW_SECTION': {
      const clonedState = cloneDeep(state);
      const formPage = clonedState?.amplifyTemplate?.pages?.find((page) => !!page.home.form);
      if (formPage) {
        formPage.home.form.showSection = action.payload;
      }
      return clonedState;
    }
    case 'SET_CONFIRMATION_PRIMARY_MESSAGE': {
      const clonedState = cloneDeep(state);
      const confirmationPage = clonedState?.amplifyTemplate?.pages?.find((page) => !!page.confirmation);
      if (confirmationPage) {
        confirmationPage.confirmation.primaryMessage = action.payload;
      }
      return clonedState;
    }
    case 'SET_CONFIRMATION_DESCRIPTION': {
      const clonedState = cloneDeep(state);
      const confirmationPage = clonedState?.amplifyTemplate?.pages?.find((page) => !!page.confirmation);
      if (confirmationPage) {
        confirmationPage.confirmation.description = action.payload;
      }
      return clonedState;
    }
    case 'SET_AMPLIFY_PERSONA_SHOW_SECTION': {
      const clonedState = cloneDeep(state);
      const personaPage = clonedState?.amplifyTemplate?.pages?.find((page) => !!page.home.persona);
      if (personaPage) {
        personaPage.home.persona.showSection = action.payload;
      }
      return clonedState;
    }
    case 'ADD_BRIEF_PAGE': {
      const clonedState = cloneDeep(state);
      if (clonedState.amplifyTemplate) {
        clonedState.amplifyTemplate.pages.push({
          brief: {
            title: 'Campaign Brief',
            description: 'Please review and agree to the details below to continue with your application.',
            hasDescription: true,
            primaryButtonLabel: 'Continue',
          }
        } as AmplifyTemplatePage);
      } else if (clonedState.pulseTemplate) {
        clonedState.pulseTemplate.pages.push({
          brief: {
            title: 'Campaign Brief',
            description: 'Please review and agree to the details below to continue with your application.',
            hasDescription: true,
            primaryButtonLabel: 'Continue',
          },
        } as PulseTemplatePage);
      }
      return clonedState;
    }
    case 'SET_BRIEF_TITLE': {
      const clonedState = cloneDeep(state);
      if (clonedState.amplifyTemplate) {
        const briefPage = clonedState?.amplifyTemplate?.pages?.find((page) => !!page.brief);
        if (briefPage) {
          briefPage.brief.title = action.payload;
        }
      } else if (clonedState.pulseTemplate) {
        const briefPage = clonedState?.pulseTemplate?.pages?.find((page) => !!page.brief);
        if (briefPage) {
          briefPage.brief.title = action.payload;
        }
      }
      return clonedState;
    }
    case 'SET_BRIEF_DESCRIPTION': {
      const clonedState = cloneDeep(state);
      if (clonedState.amplifyTemplate) {
        const briefPage = clonedState?.amplifyTemplate?.pages?.find((page) => !!page.brief);
        if (briefPage) {
          briefPage.brief.description = action.payload;
        }
      } else if (clonedState.pulseTemplate) {
        const briefPage = clonedState?.pulseTemplate?.pages?.find((page) => !!page.brief);
        if (briefPage) {
          briefPage.brief.description = action.payload;
        }
      }
      return clonedState;
    }
    case 'SET_BRIEF_HAS_DESCRIPTION': {
      const clonedState = cloneDeep(state);
      if (clonedState.amplifyTemplate) {
        const briefPage = clonedState?.amplifyTemplate?.pages?.find((page) => !!page.brief);
        if (briefPage) {
          briefPage.brief.hasDescription = action.payload;
        }
      } else if (clonedState.pulseTemplate) {
        const briefPage = clonedState?.pulseTemplate?.pages?.find((page) => !!page.brief);
        if (briefPage) {
          briefPage.brief.hasDescription = action.payload;
        }
      }
      return clonedState;
    }
    case 'SET_BRIEF_PRIMARY_BUTTON_LABEL': {
      const clonedState = cloneDeep(state);
      if (clonedState.amplifyTemplate) {
        const briefPage = clonedState?.amplifyTemplate?.pages?.find((page) => !!page.brief);
        if (briefPage) {
          briefPage.brief.primaryButtonLabel = action.payload;
        }
      } else if (clonedState.pulseTemplate) {
        const briefPage = clonedState?.pulseTemplate?.pages?.find((page) => !!page.brief);
        if (briefPage) {
          briefPage.brief.primaryButtonLabel = action.payload;
        }
      }
      return clonedState;
    }
    default:
      return state;
  }
}

interface RecruitmentContextType extends RecruitmentState {
  initPulseTemplate: (template?: GetProgramOnboardingTemplateById_template) => void;
  addPulseHeroContentCarousel: (content: string) => void;
  removePulseHeroContentCarousel: (index: number) => void;
  selectPage: (page: number) => void;
  setPulseHeroTitle: (title: string) => void;
  setPulseHeroDescription: (description: string) => void;
  setPulseHeroHasDescription: (hasDescription: boolean) => void;
  setPulseHeroListStyleType: (listStyleType: 'number' | 'bullet' | 'icon' | 'none') => void;
  addPulseHeroListItem: (item: ListItem) => void;
  removePulseHeroListItem: (index: number) => void;
  setPulseHeroListItemText: (index: number, text: string, icon: string) => void;
  setPulseHeroCtaButtonText: (text: string) => void;
  updateSettings: (settings: Partial<Settings>) => void;
  initAmplifyTemplate: (template?: GetProgramOnboardingTemplateById_template, savedFields?: IField[]) => void;
  setAmplifyHeroTitle: (title: string) => void;
  setAmplifyHeroDescription: (description: string) => void;
  setAmplifyHeroHasDescription: (hasDescription: boolean) => void;
  setAmplifyHeroPrimaryButtonLabel: (primaryButtonLabel: string) => void;
  setAmplifyAboutTitle: (title: string) => void;
  setAmplifyAboutDescription: (description: string) => void;
  setAmplifyAboutHasDescription: (hasDescription: boolean) => void;
  setAmplifyPerksTitle: (title: string) => void;
  setAmplifyPerksDescription: (description: string) => void;
  setAmplifyPerksHasDescription: (hasDescription: boolean) => void;
  setAmplifyPerksListStyleType: (listStyleType: 'number' | 'bullet' | 'icon' | 'none') => void;
  addAmplifyPerksListItem: (item: ListItem) => void;
  removeAmplifyPerksListItem: (index: number) => void;
  setAmplifyPerksListItemText: (index: number, text: string, icon: string) => void;
  setAmplifyPersonaTitle: (title: string) => void;
  setAmplifyPersonaDescription: (description: string) => void;
  setAmplifyPersonaHasDescription: (hasDescription: boolean) => void;
  setAmplifyPersonaListStyleType: (listStyleType: 'number' | 'bullet' | 'icon' | 'none') => void;
  setAmplifyPersonaImage: (image: string) => void;
  setAmplifyFormTitle: (title: string) => void;
  setAmplifyFormDescription: (description: string) => void;
  setAmplifyFormHasDescription: (hasDescription: boolean) => void;
  addAmplifyHeroContentCarousel: (content: string) => void;
  removeAmplifyHeroContentCarousel: (index: number) => void;
  setAmplifyHeroShowSection: (showSection: boolean) => void;
  setAmplifyAboutShowSection: (showSection: boolean) => void;
  setAmplifyPerksShowSection: (showSection: boolean) => void;
  setAmplifyPersonaShowSection: (showSection: boolean) => void;
  setAmplifyFormShowSection: (showSection: boolean) => void;
  addAmplifyAboutCarouselImages: (image: string) => void;
  removeAmplifyAboutCarouselImages: (index: number) => void;
  addAmplifyPersonaListItem: (item: ListItem) => void;
  removeAmplifyPersonaListItem: (index: number) => void;
  setAmplifyPersonaListItemText: (index: number, text: string, icon: string) => void;
  setFormFields: (fields: IField[]) => void;
  addFormField: (field: IField) => void;
  removeFormField: (fieldId: number) => void;
  editFormField: (oldFieldId: number, newField: IField) => void;
  reorderFormFields: (fields: IField[]) => void;
  setShowNewFormFieldModal: (show: boolean) => void;
  setConfirmationPrimaryMessage: (primaryMessage: string) => void;
  setConfirmationDescription: (description: string) => void;
  addBriefPage: () => void;
  setBriefTitle: (title: string) => void;
  setBriefDescription: (description: string) => void;
  setBriefHasDescription: (hasDescription: boolean) => void;
  setBriefPrimaryButtonLabel: (primaryButtonLabel: string) => void;
}

const initialState: RecruitmentState = {
  pulseTemplate: null,
  amplifyTemplate: null,
  selectedPage: 0,
  showNewFormFieldModal: false,
  settings: {
    applicationTitle: '',
    logo: '',
    brandName: '',
    campaignDates: {
      startDate: new Date(),
      endDate: new Date(),
    },
    socialAccounts: {
      instagram: '',
      tiktok: '',
      youtube: '',
      pinterest: '',
      website: '',
    },
    pageUrl: '',
    bgPrimary: '#ff4800',
    bgSecondary: '#AB8A7D',
    titlePrimary: '#FFFFFF',
    titleSecondary: '#FFFFFF',
    bodyPrimary: '#FFFFFF',
    bodySecondary: '#FFFFFF',
    buttonBg: '#AB8A7D',
    buttonTextColor: '#FFFFFF',
    titleFont: {
      fontFamily: '',
      fontSize: 0,
    },
    bodyFont: {
      fontFamily: '',
      fontSize: 0,
    },
    buttonFont: {
      fontFamily: '',
      fontSize: 0,
    },
  },
};

export const RecruitmentContext = createContext<RecruitmentContextType>({
  ...initialState,
  initPulseTemplate: () => {},
  addPulseHeroContentCarousel: () => {},
  removePulseHeroContentCarousel: () => {},
  selectPage: () => {},
  setPulseHeroTitle: () => {},
  setPulseHeroDescription: () => {},
  setPulseHeroHasDescription: () => {},
  setPulseHeroListStyleType: () => {},
  addPulseHeroListItem: () => {},
  removePulseHeroListItem: () => {},
  setPulseHeroListItemText: () => {},
  setPulseHeroCtaButtonText: () => {},
  updateSettings: () => {},
  initAmplifyTemplate: () => {},
  setAmplifyHeroTitle: () => {},
  setAmplifyHeroDescription: () => {},
  setAmplifyHeroHasDescription: () => {},
  setAmplifyHeroPrimaryButtonLabel: () => {},
  setAmplifyAboutTitle: () => {},
  setAmplifyAboutDescription: () => {},
  setAmplifyAboutHasDescription: () => {},
  setAmplifyPerksTitle: () => {},
  setAmplifyPerksDescription: () => {},
  setAmplifyPerksHasDescription: () => {},
  setAmplifyPerksListStyleType: () => {},
  addAmplifyPerksListItem: () => {},
  removeAmplifyPerksListItem: () => {},
  setAmplifyPerksListItemText: () => {},
  setAmplifyPersonaTitle: () => {},
  setAmplifyPersonaDescription: () => {},
  setAmplifyPersonaHasDescription: () => {},
  setAmplifyPersonaListStyleType: () => {},
  setAmplifyPersonaImage: () => {},
  setAmplifyFormTitle: () => {},
  setAmplifyFormDescription: () => {},
  setAmplifyFormHasDescription: () => {},
  addAmplifyHeroContentCarousel: () => {},
  removeAmplifyHeroContentCarousel: () => {},
  setAmplifyHeroShowSection: () => {},
  setAmplifyAboutShowSection: () => {},
  setAmplifyPerksShowSection: () => {},
  setAmplifyPersonaShowSection: () => {},
  setAmplifyFormShowSection: () => {},
  addAmplifyAboutCarouselImages: () => {},
  removeAmplifyAboutCarouselImages: () => {},
  addAmplifyPersonaListItem: () => {},
  removeAmplifyPersonaListItem: () => {},
  setAmplifyPersonaListItemText: () => {},
  setFormFields: () => {},
  addFormField: () => {},
  removeFormField: () => {},
  editFormField: () => {},
  reorderFormFields: () => {},
  setShowNewFormFieldModal: () => {},
  setConfirmationPrimaryMessage: () => {},
  setConfirmationDescription: () => {},
  addBriefPage: () => {},
  setBriefTitle: () => {},
  setBriefDescription: () => {},
  setBriefHasDescription: () => {},
  setBriefPrimaryButtonLabel: () => {},
});

export const RecruitmentProvider: React.FC = ({ children }) => {
  const [state, dispatch] = useReducer(recruitmentReducer, initialState);

  const actions = useMemo(
    () => ({
      initPulseTemplate: (template?: GetProgramOnboardingTemplateById_template) => dispatch({ type: 'INIT_PULSE_TEMPLATE', payload: template }),
      addPulseHeroContentCarousel: (content: string) => dispatch({ type: 'ADD_PULSE_HERO_CONTENT_CAROUSEL', payload: content }),
      removePulseHeroContentCarousel: (index: number) => dispatch({ type: 'REMOVE_PULSE_HERO_CONTENT_CAROUSEL', payload: index }),
      selectPage: (page: number) => dispatch({ type: 'SELECT_PAGE', payload: page }),
      setPulseHeroTitle: (title: string) => dispatch({ type: 'SET_PULSE_HERO_TITLE', payload: title }),
      setPulseHeroDescription: (description: string) => dispatch({ type: 'SET_PULSE_HERO_DESCRIPTION', payload: description }),
      setPulseHeroHasDescription: (hasDescription: boolean) => dispatch({ type: 'SET_PULSE_HERO_HAS_DESCRIPTION', payload: hasDescription }),
      setPulseHeroListStyleType: (listStyleType: 'number' | 'bullet' | 'icon' | 'none') => dispatch({ type: 'SET_PULSE_HERO_LIST_STYLE_TYPE', payload: listStyleType }),
      addPulseHeroListItem: (item: ListItem) => dispatch({ type: 'ADD_PULSE_HERO_LIST_ITEM', payload: item }),
      removePulseHeroListItem: (index: number) => dispatch({ type: 'REMOVE_PULSE_HERO_LIST_ITEM', payload: index }),
      setPulseHeroListItemText: (index: number, text: string, icon: string) => dispatch({ type: 'SET_PULSE_HERO_LIST_ITEM_TEXT', payload: { index, text, icon } }),
      setPulseHeroCtaButtonText: (text: string) => dispatch({ type: 'SET_PULSE_HERO_CTA_BUTTON_TEXT', payload: text }),
      updateSettings: (settings: Partial<Settings>) => dispatch({ type: 'UPDATE_SETTINGS', payload: settings }),
      initAmplifyTemplate: (template?: GetProgramOnboardingTemplateById_template, savedFields?: IField[]) => dispatch({ type: 'INIT_AMPLIFY_TEMPLATE', payload: { template, savedFields } }),
      setAmplifyHeroTitle: (title: string) => dispatch({ type: 'SET_AMPLIFY_HERO_TITLE', payload: title }),
      setAmplifyHeroDescription: (description: string) => dispatch({ type: 'SET_AMPLIFY_HERO_DESCRIPTION', payload: description }),
      setAmplifyHeroHasDescription: (hasDescription: boolean) => dispatch({ type: 'SET_AMPLIFY_HERO_HAS_DESCRIPTION', payload: hasDescription }),
      setAmplifyHeroPrimaryButtonLabel: (primaryButtonLabel: string) => dispatch({ type: 'SET_AMPLIFY_HERO_PRIMARY_BUTTON_LABEL', payload: primaryButtonLabel }),
      setAmplifyAboutTitle: (title: string) => dispatch({ type: 'SET_AMPLIFY_ABOUT_TITLE', payload: title }),
      setAmplifyAboutDescription: (description: string) => dispatch({ type: 'SET_AMPLIFY_ABOUT_DESCRIPTION', payload: description }),
      setAmplifyAboutHasDescription: (hasDescription: boolean) => dispatch({ type: 'SET_AMPLIFY_ABOUT_HAS_DESCRIPTION', payload: hasDescription }),
      setAmplifyPerksTitle: (title: string) => dispatch({ type: 'SET_AMPLIFY_PERKS_TITLE', payload: title }),
      setAmplifyPerksDescription: (description: string) => dispatch({ type: 'SET_AMPLIFY_PERKS_DESCRIPTION', payload: description }),
      setAmplifyPerksHasDescription: (hasDescription: boolean) => dispatch({ type: 'SET_AMPLIFY_PERKS_HAS_DESCRIPTION', payload: hasDescription }),
      setAmplifyPerksListStyleType: (listStyleType: 'number' | 'bullet' | 'icon' | 'none') => dispatch({ type: 'SET_AMPLIFY_PERKS_LIST_STYLE_TYPE', payload: listStyleType }),
      addAmplifyPerksListItem: (item: ListItem) => dispatch({ type: 'ADD_AMPLIFY_PERKS_LIST_ITEM', payload: item }),
      removeAmplifyPerksListItem: (index: number) => dispatch({ type: 'REMOVE_AMPLIFY_PERKS_LIST_ITEM', payload: index }),
      setAmplifyPerksListItemText: (index: number, text: string, icon: string) => dispatch({ type: 'SET_AMPLIFY_PERKS_LIST_ITEM_TEXT', payload: { index, text, icon } }),
      setAmplifyPersonaTitle: (title: string) => dispatch({ type: 'SET_AMPLIFY_PERSONA_TITLE', payload: title }),
      setAmplifyPersonaDescription: (description: string) => dispatch({ type: 'SET_AMPLIFY_PERSONA_DESCRIPTION', payload: description }),
      setAmplifyPersonaHasDescription: (hasDescription: boolean) => dispatch({ type: 'SET_AMPLIFY_PERSONA_HAS_DESCRIPTION', payload: hasDescription }),
      setAmplifyPersonaListStyleType: (listStyleType: 'number' | 'bullet' | 'icon' | 'none') => dispatch({ type: 'SET_AMPLIFY_PERSONA_LIST_STYLE_TYPE', payload: listStyleType }),
      setAmplifyPersonaImage: (image: string) => dispatch({ type: 'SET_AMPLIFY_PERSONA_IMAGE', payload: image }),
      setAmplifyFormTitle: (title: string) => dispatch({ type: 'SET_AMPLIFY_FORM_TITLE', payload: title }),
      setAmplifyFormDescription: (description: string) => dispatch({ type: 'SET_AMPLIFY_FORM_DESCRIPTION', payload: description }),
      setAmplifyFormHasDescription: (hasDescription: boolean) => dispatch({ type: 'SET_AMPLIFY_FORM_HAS_DESCRIPTION', payload: hasDescription }),
      addAmplifyHeroContentCarousel: (content: string) => dispatch({ type: 'ADD_AMPLIFY_HERO_CONTENT_CAROUSEL', payload: content }),
      removeAmplifyHeroContentCarousel: (index: number) => dispatch({ type: 'REMOVE_AMPLIFY_HERO_CONTENT_CAROUSEL', payload: index }),
      setAmplifyHeroShowSection: (showSection: boolean) => dispatch({ type: 'SET_AMPLIFY_HERO_SHOW_SECTION', payload: showSection }),
      setAmplifyAboutShowSection: (showSection: boolean) => dispatch({ type: 'SET_AMPLIFY_ABOUT_SHOW_SECTION', payload: showSection }),
      setAmplifyPerksShowSection: (showSection: boolean) => dispatch({ type: 'SET_AMPLIFY_PERKS_SHOW_SECTION', payload: showSection }),
      setAmplifyPersonaShowSection: (showSection: boolean) => dispatch({ type: 'SET_AMPLIFY_PERSONA_SHOW_SECTION', payload: showSection }),
      setAmplifyFormShowSection: (showSection: boolean) => dispatch({ type: 'SET_AMPLIFY_FORM_SHOW_SECTION', payload: showSection }),
      addAmplifyAboutCarouselImages: (image: string) => dispatch({ type: 'ADD_AMPLIFY_ABOUT_CAROUSEL_IMAGES', payload: image }),
      removeAmplifyAboutCarouselImages: (index: number) => dispatch({ type: 'REMOVE_AMPLIFY_ABOUT_CAROUSEL_IMAGES', payload: index }),
      addAmplifyPersonaListItem: (item: ListItem) => dispatch({ type: 'ADD_AMPLIFY_PERSONA_LIST_ITEM', payload: item }),
      removeAmplifyPersonaListItem: (index: number) => dispatch({ type: 'REMOVE_AMPLIFY_PERSONA_LIST_ITEM', payload: index }),
      setAmplifyPersonaListItemText: (index: number, text: string, icon: string) => dispatch({ type: 'SET_AMPLIFY_PERSONA_LIST_ITEM_TEXT', payload: { index, text, icon } }),
      setFormFields: (fields: IField[]) => dispatch({ type: 'SET_FORM_FIELDS', payload: fields }),
      addFormField: (field: IField) => dispatch({ type: 'ADD_FORM_FIELD', payload: field }),
      removeFormField: (fieldId: number) => dispatch({ type: 'REMOVE_FORM_FIELD', payload: fieldId }),
      editFormField: (oldFieldId: number, newField: IField) => dispatch({ type: 'EDIT_FORM_FIELD', payload: { oldFieldId, newField } }),
      reorderFormFields: (fields: IField[]) => dispatch({ type: 'REORDER_FORM_FIELDS', payload: fields }),
      setShowNewFormFieldModal: (show: boolean) => dispatch({ type: 'SET_SHOW_NEW_FORM_FIELD_MODAL', payload: show }),
      setConfirmationPrimaryMessage: (primaryMessage: string) => dispatch({ type: 'SET_CONFIRMATION_PRIMARY_MESSAGE', payload: primaryMessage }),
      setConfirmationDescription: (description: string) => dispatch({ type: 'SET_CONFIRMATION_DESCRIPTION', payload: description }),
      addBriefPage: () => dispatch({ type: 'ADD_BRIEF_PAGE' }),
      setBriefTitle: (title: string) => dispatch({ type: 'SET_BRIEF_TITLE', payload: title }),
      setBriefDescription: (description: string) => dispatch({ type: 'SET_BRIEF_DESCRIPTION', payload: description }),
      setBriefHasDescription: (hasDescription: boolean) => dispatch({ type: 'SET_BRIEF_HAS_DESCRIPTION', payload: hasDescription }),
      setBriefPrimaryButtonLabel: (primaryButtonLabel: string) => dispatch({ type: 'SET_BRIEF_PRIMARY_BUTTON_LABEL', payload: primaryButtonLabel }),
    }),
    [dispatch],
  );

  const contextValue = useMemo(
    () => ({
      ...state,
      ...actions,
    }),
    [state, actions],
  );

  return (
    <RecruitmentContext.Provider value={contextValue}>
      {children}
    </RecruitmentContext.Provider>
  );
};

export const useRecruitment = () => {
  const context = useContext(RecruitmentContext);
  if (!context) {
    throw new Error('useRecruitment must be used within a RecruitmentProvider');
  }
  return context;
};

export const isPulsePage = (page: PulseTemplatePage | AmplifyTemplatePage): page is PulseTemplatePage => {
  return 'hero' in page;
};

export const isAmplifyPage = (page: PulseTemplatePage | AmplifyTemplatePage): page is AmplifyTemplatePage => {
  return 'home' in page;
};

export const getPageType = (page: PulseTemplatePage | AmplifyTemplatePage) => {
  if (isPulsePage(page)) {
    if (page.hero) {
      return 'hero';
    }
  }
  if (isAmplifyPage(page)) {
    if (page.home) {
      return 'home';
    }
  }
  if (page.confirmation) {
    return 'confirmation';
  }
  if (page.brief) {
    return 'brief';
  }
  return 'unknown';
};

export const useSelectedPageType = () => {
  const { pulseTemplate, amplifyTemplate, selectedPage } = useRecruitment();
  const page = pulseTemplate?.pages[selectedPage] || amplifyTemplate?.pages[selectedPage];
  if (!page) {
    return 'unknown';
  }
  return getPageType(page);
};

export const usePulseHeroPage = () => {
  const { pulseTemplate, selectedPage } = useRecruitment();
  const page = pulseTemplate?.pages[selectedPage];
  return page?.hero;
};

export const useAmplifyHomePage = () => {
  const { amplifyTemplate, selectedPage } = useRecruitment();
  const page = amplifyTemplate?.pages[selectedPage];
  return page?.home;
};

export const useConfirmationPage = () => {
  const { pulseTemplate, amplifyTemplate, selectedPage } = useRecruitment();
  const page = pulseTemplate?.pages[selectedPage] || amplifyTemplate?.pages[selectedPage];
  return page?.confirmation;
};

export const useBriefPage = () => {
  const { pulseTemplate, amplifyTemplate, selectedPage } = useRecruitment();
  const page = pulseTemplate?.pages[selectedPage] || amplifyTemplate?.pages[selectedPage];
  return page?.brief;
};

export const transformTemplateToPulseTemplate = (template: GetProgramOnboardingTemplateById_template): PulseTemplate => {
  const templateWithoutTypeName = omit({ ...template.config }, '__typename');
  const sortedPages = Object.entries(templateWithoutTypeName).sort((a, b) => a[1]?.order - b[1]?.order);

  return {
    pages: sortedPages.map(([key, value]) => {
      const typedKey = key as keyof typeof template.config;
      switch (typedKey) {
        case 'hero': {
          const hero = value as GetProgramOnboardingTemplateById_template_config_hero;
          return {
            hero: {
              carouselImages: hero.carouselImages,
              title: hero?.title || '',
              description: hero?.description || '',
              hasDescription: !!hero?.description || false,
              listStyleType: hero?.listStyleType,
              listItems: hero?.listItems?.map((item) => ({
                text: item.text,
                icon: item.icon,
              })),
              ctaButtonText: hero?.ctaButtonText || '',
            }
          };
        }
        case 'confirmation': {
          const confirmation = value as GetProgramOnboardingTemplateById_template_config_confirmation;
          return {
            confirmation: {
              primaryMessage: confirmation?.primaryMessage || '',
              description: confirmation?.description || '',
            }
          };
        }
        case 'brief': {
          const brief = value as GetProgramOnboardingTemplateById_template_config_brief;
          return {
            brief: {
              title: brief?.title || '',
              description: brief?.description || '',
              hasDescription: !!brief?.description || false,
              primaryButtonLabel: brief?.ctaButtonText || '',
            }
          };
        }
        default: {
          return null;
        }
      }
    }).filter(Boolean) as PulseTemplatePage[],
  };
};

export const transformTemplateToAmplifyTemplate = (template: GetProgramOnboardingTemplateById_template, savedFields?: IField[]): AmplifyTemplate => {
  const templateWithoutTypeName = omit({ ...template.config }, '__typename');
  const sortedPages = Object.entries(templateWithoutTypeName).sort((a, b) => a[1]?.order - b[1]?.order);

  return {
    pages: sortedPages.map(([key, value]) => {
      const typedKey = key as keyof typeof template.config;
      switch (typedKey) {
        case 'hero': {
          return {
            home: {
              hero: {
                carouselImages: template.config.hero.carouselImages,
                title: template.config.hero.title,
                description: template.config.hero.description,
                hasDescription: !!template.config.hero.description,
                primaryButtonLabel: template.config.hero.ctaButtonText,
                showSection: template.config.hero.isVisible || false,
              },
              about: {
                carouselImages: template.config.about.images,
                title: template.config.about.title,
                description: template.config.about.description,
                hasDescription: !!template.config.about.description,
                showSection: template.config.about.isVisible || false,
              },
              perks: {
                title: template.config.perks.title,
                description: template.config.perks.description,
                hasDescription: !!template.config.perks.description,
                listStyleType: template.config.perks.listStyleType as 'number' | 'bullet' | 'icon' | 'none',
                listItems: template.config.perks.listItems.map((item) => ({
                  text: item.text,
                  icon: item.icon,
                })),
                showSection: template.config.perks.isVisible || false,
              },
              persona: {
                title: template.config.persona.title,
                description: template.config.persona.description,
                hasDescription: !!template.config.persona.description,
                listStyleType: template.config.persona.listStyleType as 'number' | 'bullet' | 'icon' | 'none',
                listItems: template.config.persona.listItems.map((item) => ({
                  text: item.text,
                  icon: item.icon,
                })),
                personaImage: template.config.persona.images?.[0],
                showSection: template.config.persona.isVisible || false,
              },
              form: {
                title: template.config.forms.title,
                description: template.config.forms.description,
                hasDescription: !!template.config.forms.description,
                showSection: template.config.forms.isVisible || false,
                fields: savedFields || [],
              },
            },
          };
        }
        case 'confirmation': {
          const confirmation = value as GetProgramOnboardingTemplateById_template_config_confirmation;
          return {
            confirmation: {
              primaryMessage: confirmation?.primaryMessage || '',
              description: confirmation?.description || '',
            }
          };
        }
        case 'brief': {
          const brief = value as GetProgramOnboardingTemplateById_template_config_brief;
          return {
            brief: {
              title: brief?.title || '',
              description: brief?.description || '',
              hasDescription: !!brief?.description || false,
              primaryButtonLabel: brief?.ctaButtonText || '',
            }
          };
        }
        default: {
          return null;
        }
      }
    }).filter(Boolean) as AmplifyTemplatePage[],
  };
};

export const transformTemplateToState = (template: GetProgramOnboardingTemplateById_template, savedFields?: IField[]): RecruitmentState => {
  return {
    settings: {
      applicationTitle: template.applicationTitle,
      logo: template.brandLogo,
      brandName: template.brandName,
      campaignDates: {
        startDate: template.startDate ? new Date(template.startDate) : undefined,
        endDate: template.endDate ? new Date(template.endDate) : undefined,
      },
      socialAccounts: {
        instagram: template.socialMediaHandles.find((handle) => handle.type === 'INSTAGRAM')?.handle || '',
        tiktok: template.socialMediaHandles.find((handle) => handle.type === 'TIKTOK')?.handle || '',
        youtube: template.socialMediaHandles.find((handle) => handle.type === 'YOUTUBE')?.handle || '',
        pinterest: template.socialMediaHandles.find((handle) => handle.type === 'PINTEREST')?.handle || '',
        website: template.socialMediaHandles.find((handle) => handle.type === 'WEBSITE')?.handle || '',
      },
      pageUrl: template.pageUrl,
      bgPrimary: template.styles.pagePrimaryColor,
      bgSecondary: template.styles.pageSecondaryColor,
      titlePrimary: template.styles.textStyle.titleStyle.primaryColor,
      titleSecondary: template.styles.textStyle.titleStyle.secondaryColor,
      bodyPrimary: template.styles.textStyle.bodyStyle.primaryColor,
      bodySecondary: template.styles.textStyle.bodyStyle.secondaryColor,
      buttonBg: template.styles.ctaButtonStyle.buttonColor,
      buttonTextColor: template.styles.ctaButtonStyle.textColor,
      titleFont: {
        fontFamily: template.styles.textStyle.titleStyle.fontFamily,
        fontSize: template.styles.textStyle.titleStyle.fontSize,
      },
      bodyFont: {
        fontFamily: template.styles.textStyle.bodyStyle.fontFamily,
        fontSize: template.styles.textStyle.bodyStyle.fontSize,
      },
      buttonFont: {
        fontFamily: template.styles.ctaButtonStyle.fontFamily,
        fontSize: template.styles.ctaButtonStyle.fontSize,
      },
    },
    pulseTemplate: template.templateType === ProgramOnboardingTemplateType.PULSE ? transformTemplateToPulseTemplate(template) : null,
    amplifyTemplate: template.templateType === ProgramOnboardingTemplateType.AMPLIFY ? transformTemplateToAmplifyTemplate(template, savedFields) : null,
    selectedPage: 0,
    showNewFormFieldModal: false,
  };
};

export const transformPulseTemplateToPayload = (template: PulseTemplate, settings: Settings, logoImage: string, heroCarouselImages: string[]): ProgramOnboardingTemplateInput => {
  return {
    applicationTitle: settings.applicationTitle,
    brandName: settings.brandName,
    brandLogo: logoImage,
    startDate: settings.campaignDates.startDate,
    endDate: settings.campaignDates.endDate,
    socialMediaHandles: Object.entries(settings.socialAccounts).map(([key, value]) => ({
      type: key,
      handle: value,
    })),
    pageUrl: settings.pageUrl,
    isPublished: false,
    config: {
      hero: {
        title: template.pages[0].hero.title,
        description: template.pages[0].hero.description,
        carouselImages: heroCarouselImages,
        ctaButtonText: '',
        listItems: template.pages[0].hero.listItems,
        listStyleType: template.pages[0].hero.listStyleType,
        isVisible: true,
        order: 1,
      },
      about: null,
      perks: null,
      persona: null,
      forms: null,
      brief: null,
      products: null,
      confirmation: null,
    },
    styles: {
      pagePrimaryColor: settings.bgPrimary,
      pageSecondaryColor: settings.bgSecondary,
      textStyle: {
        titleStyle: {
          fontFamily: settings.titleFont.fontFamily,
          fontSize: settings.titleFont.fontSize,
          primaryColor: settings.titlePrimary,
          secondaryColor: settings.titleSecondary,
        },
        bodyStyle: {
          fontFamily: settings.bodyFont.fontFamily,
          fontSize: settings.bodyFont.fontSize,
          primaryColor: settings.bodyPrimary,
          secondaryColor: settings.bodySecondary,
        },
      },
      ctaButtonStyle: {
        buttonColor: settings.buttonBg,
        fontFamily: settings.buttonFont.fontFamily,
        fontSize: settings.buttonFont.fontSize,
        textColor: settings.buttonTextColor,
      },
    },
    templateType: ProgramOnboardingTemplateType.PULSE,
  };
};

export const transformAmplifyTemplateToPayload = (template: AmplifyTemplate, settings: Settings, logoImage: string, heroCarouselImages: string[], aboutContentImages: string[], personaImage: string): ProgramOnboardingTemplateInput => {
  const briefPageIndex = template.pages.findIndex((page) => !!page.brief);
  const briefPage = template.pages[briefPageIndex];

  const confirmationPage = template.pages.find((page) => !!page.confirmation);

  return {
    applicationTitle: settings.applicationTitle,
    brandName: settings.brandName,
    brandLogo: logoImage || '',
    startDate: settings.campaignDates.startDate,
    endDate: settings.campaignDates.endDate,
    socialMediaHandles: Object.entries(settings.socialAccounts).map(([key, value]) => ({
      type: key,
      handle: value || '',
    })),
    pageUrl: settings.pageUrl,
    isPublished: false,
    config: {
      hero: {
        title: template.pages[0].home.hero.title,
        description: template.pages[0].home.hero.description,
        carouselImages: heroCarouselImages,
        ctaButtonText: template.pages[0].home.hero.primaryButtonLabel,
        isVisible: template.pages[0].home.hero.showSection,
        order: 1
      },
      about: {
        title: template.pages[0].home.about.title,
        description: template.pages[0].home.about.description,
        images: aboutContentImages,
        isVisible: template.pages[0].home.about.showSection,
        order: 1,
      },
      forms: {
        title: template.pages[0].home.form.title,
        description: template.pages[0].home.form.description,
        isVisible: template.pages[0].home.form.showSection,
        applicationFormFields: {
          memberFieldSchemas: transformFieldsToMemberFieldSchema(template.pages[0].home.form.fields),
          dbColumns: transformFieldsToDbColumns(template.pages[0].home.form.fields),
        },
        order: 1,
      },
      perks: {
        title: template.pages[0].home.perks.title,
        description: template.pages[0].home.perks.description,
        listStyleType: template.pages[0].home.perks.listStyleType,
        listItems: template.pages[0].home.perks.listItems.map((item) => ({
          text: item.text,
          icon: item.icon,
        })),
        isVisible: template.pages[0].home.perks.showSection,
        order: 1,
      },
      persona: {
        title: template.pages[0].home.persona.title,
        description: template.pages[0].home.persona.description,
        images: personaImage ? [personaImage] : [],
        listItems: template.pages[0].home.persona.listItems.map((item) => ({
          text: item.text,
          icon: item.icon,
        })),
        listStyleType: template.pages[0].home.persona.listStyleType,
        isVisible: template.pages[0].home.persona.showSection,
        order: 1,
      },
      brief: briefPage ? {
        title: briefPage.brief.title,
        description: briefPage.brief.description,
        ctaButtonText: briefPage.brief.primaryButtonLabel,
        order: briefPageIndex + 1,
      } : null,
      products: null,
      confirmation: confirmationPage ? {
        primaryMessage: confirmationPage.confirmation.primaryMessage,
        description: confirmationPage.confirmation.description,
        order: 9999,
      } : null,
    },
    styles: {
      pagePrimaryColor: settings.bgPrimary,
      pageSecondaryColor: settings.bgSecondary,
      textStyle: {
        titleStyle: {
          fontFamily: settings.titleFont.fontFamily,
          fontSize: settings.titleFont.fontSize,
          primaryColor: settings.titlePrimary,
          secondaryColor: settings.titleSecondary,
        },
        bodyStyle: {
          fontFamily: settings.bodyFont.fontFamily,
          fontSize: settings.bodyFont.fontSize,
          primaryColor: settings.bodyPrimary,
          secondaryColor: settings.bodySecondary,
        },
      },
      ctaButtonStyle: {
        buttonColor: settings.buttonBg,
        fontFamily: settings.buttonFont.fontFamily,
        fontSize: settings.buttonFont.fontSize,
        textColor: settings.buttonTextColor,
      },
    },
    templateType: ProgramOnboardingTemplateType.AMPLIFY,
  };
};

export const transformFieldsToMemberFieldSchema = (fields: IField[]): ProgramOnboardingMemberFieldSchemaInput[] => {
  return fields.filter((field) => field.source === 'schema').map((field) => ({
    schemaId: field.id,
    required: field.required,
    label: field.label,
    order: field.order,
  }));
};

export const transformFieldsToDbColumns = (fields: IField[]): DbColumnInput[] => {
  return fields.filter((field) => field.source === 'dbColumn').map((field) => ({
    name: field.name,
    required: field.required,
    label: field.label,
    order: field.order,
  }));
};

export const getPageImage = (page: PulseTemplatePage | AmplifyTemplatePage): string => {
  if (isPulsePage(page)) {
    return HomeImage;
  }
  if (isAmplifyPage(page)) {
    return HomeImage;
  }
  if ('confirmation' in page) {
    return ConfirmationImage;
  }
  if ('brief' in page) {
    return BriefPageImage;
  }
  return '';
};

export const getPageName = (page: PulseTemplatePage | AmplifyTemplatePage): string => {
  if (isPulsePage(page)) {
    return 'Home';
  }
  if (isAmplifyPage(page)) {
    return 'Home';
  }
  if ('confirmation' in page) {
    return 'Confirmation';
  }
  if ('brief' in page) {
    return 'Brief';
  }
  return '';
};

export const useFullPreviewUrl = () => {
  const { settings } = useRecruitment();

  const { clientInfo } = useAuth();

  const {
    client = null,
  } = useGetCurrentClient();

  const clientHostname = useMemo(() => client?.hostname, [client]);

  const baseUrl = useMemo(() => {
    if (typeof window === 'undefined') {
      return '';
    }
    const url = `${window.location.origin}/join/`;
    return clientHostname ? url.replace('community', clientHostname) : url;
  }, [clientHostname]);

  const fullUrl = useMemo(() => {
    const landingPagePath = encodeURIComponent(settings.pageUrl || '');
    const url = `${baseUrl}${landingPagePath}`;
    return clientHostname ? url : `${url}?clientId=${clientInfo?.id}`;
  }, [baseUrl, clientHostname, clientInfo?.id, settings.pageUrl]);

  return fullUrl;
};
